'use client'
import { useChannelChildrenInfinite } from '@/app/(site)/[username]/channel-children'
import { addGifsToChannel } from '@/app/util/channel-api'
import { EditContext } from '@/context/edit-manager'
import { FlashMessageContext } from '@/context/flash-message'
import UserContext from '@/context/user-context'
import { cn } from '@/util/cn'
import { IChannel } from '@giphy/js-types'
import { useContext } from 'react'
import Loader from '../../loader'
import { ModalContext } from '../../modal'
import AllGifsGrid from '../all-gifs-grid'
import AddToCollectionCallout from '../callouts'
import NewCollectionButton from '../new-collection-button'
import SearchInput from '../search-input'
import { cardClassName, ChannelContent, colorsClassName, MoreChildrenBadge } from '../ui'

type Props = { channelId?: IChannel['id']; gifId?: string }

const peerClassName = 'group w-36'

export const AddGifToCollectionButton = ({
    className,
    children,
    gifId,
    channelId,
}: {
    className?: string
    children: React.ReactNode
    gifId?: string
    channelId: number
}) => {
    const { showStatusMessage } = useContext(FlashMessageContext)
    const { setModalType } = useContext(ModalContext)
    return (
        <div
            className={cn(className)}
            onClick={async () => {
                if (gifId) {
                    const result = await addGifsToChannel([gifId], channelId)
                    showStatusMessage('Added to collection', 'Failed to add to collection', result.ok)
                    setModalType(null)
                }
            }}
        >
            {children}
        </div>
    )
}

const AddToCollection = ({ channelId: channelId, gifId: id }: Props) => {
    const { user } = useContext(UserContext)
    const isSubChannel = !!channelId
    const { setModalType, modal } = useContext(ModalContext)
    const { staffMode } = useContext(EditContext)

    const currentChannelId = channelId || modal?.channelId || user?.channel_id
    const { channel, isLoading, ref, children, doneFetching } = useChannelChildrenInfinite(currentChannelId!)
    if (!user) return null
    const rootChannel = channel?.id === user?.channel_id
    return (
        <div className="flex flex-col gap-2 p-2">
            {staffMode && (
                <SearchInput
                    onSelect={(channel) => {
                        setModalType({
                            name: 'addToCollection',
                            channelId: channel.id,
                            title: `Add to ${channel.display_name}`,
                        })
                    }}
                />
            )}
            <div
                className="flex animate-fade-in flex-wrap justify-start gap-4 overflow-x-hidden overflow-y-auto pt-2"
                style={{ maxHeight: 'calc(100vh - 300px)' }}
                key={`container-${isLoading}`}
            >
                {isLoading ? (
                    <Loader />
                ) : (
                    <>
                        {isSubChannel && rootChannel && modal?.name !== 'addToCollection' && (
                            <AddToCollectionCallout channel={channel} className={''} />
                        )}
                        {channel && <NewCollectionButton className={cn(peerClassName)} channel={channel} />}
                        {children?.map((subChannel) => {
                            const cClassName = cn(cardClassName, peerClassName, colorsClassName)
                            return subChannel.num_children === 0 ? (
                                <AddGifToCollectionButton
                                    channelId={subChannel.id}
                                    gifId={id}
                                    className={cClassName}
                                    key={subChannel.id}
                                >
                                    <ChannelContent channel={subChannel} />
                                </AddGifToCollectionButton>
                            ) : (
                                <div
                                    key={subChannel.id}
                                    className={cClassName}
                                    onClick={() => {
                                        setModalType({
                                            name: 'addToCollection',
                                            channelId: subChannel.id,
                                            title: subChannel.display_name,
                                        })
                                    }}
                                >
                                    <ChannelContent channel={subChannel} />
                                    <MoreChildrenBadge count={subChannel.num_children} />
                                </div>
                            )
                        })}
                        {!doneFetching && (
                            <div ref={ref}>
                                <Loader />
                            </div>
                        )}
                    </>
                )}
            </div>
            {modal?.name !== 'addToCollection' && <AllGifsGrid channel={channel} />}
        </div>
    )
}
export default AddToCollection
